import React from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { CommonModalV1, CommonButtonV1, CommonTextFieldV1, CommonTextField } from 'gemlux-common-components';
import { Box, Typography } from '@mui/material';
import DeleteSvg from '../../../assets/img/delete-icon-new.svg';
import WarningSvg from '../../../assets/img/warning-round-icon.svg';

import { theme } from '../../../theme/theme';
import { validation } from '../../../schema/validation';

interface DeleteChatModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (_text?: string) => void;
  chatData: {
    chatTitle: string;
    chatTimestamp: string;
  };
  isDeleteChat?: boolean;
}

export default function DeleteOrCreateNewChatModal({
  open,
  onClose,
  onConfirm,
  chatData,
  isDeleteChat,
}: DeleteChatModalProps) {
  const formik = useFormik({
    initialValues: {
      chatTitle: '',
    },
    validationSchema: yup.object({
      chatTitle: validation.chatTitle,
    }),
    onSubmit: (values) => {
      onConfirm(values.chatTitle);
      formik.resetForm();
    },
  });

  return (
    <CommonModalV1
      align="center"
      modal
      open={open}
      Icon={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '64px',
            height: '64px',
            mt: '16px',
          }}
        >
          <Box
            component="img"
            src={isDeleteChat ? DeleteSvg : WarningSvg}
            alt={isDeleteChat ? 'Delete Icon' : 'Warning Icon'}
          />
        </Box>
      }
    >
      {isDeleteChat ? (
        <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '32px', mt: '-24px' }}>
          <Typography variant="textH5">Are you sure you want to delete following chat?</Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
            <Typography variant="textBaseLg500" textTransform="capitalize">
              {chatData.chatTitle}{' '}
            </Typography>

            <Typography variant="textBase500" color="#525E6F">
              {new Date(chatData.chatTimestamp).toLocaleDateString('en-GB')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
            <CommonButtonV1
              fullWidth
              variant="contained"
              backgroundColor={theme.palette.shades.errorBrightMain}
              onClick={() => {
                onConfirm();
              }}
            >
              <Typography variant="textBase500" color="HighlightText">
                {' '}
                Yes,Delete
              </Typography>
            </CommonButtonV1>
            <CommonButtonV1
              fullWidth
              variant="outlined"
              backgroundColor={theme.palette.white.main}
              onClick={onClose}
              sx={{ border: `1px solid ${theme.palette.shades.primaryBrightDark}` }}
            >
              <Typography variant="textBase500" color={theme.palette.shades.primaryBrightDark}>
                {' '}
                Cancel
              </Typography>
            </CommonButtonV1>
          </Box>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '32px', mt: '-24px' }}>
          <Typography variant="textH5">New Chat Topic</Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              alignItems: 'flex-start',
            }}
          >
            <Typography variant="textBase500">Chat Title</Typography>
            <CommonTextField
              fullWidth
              type="text"
              name="chatTitle"
              onBlur={formik.handleBlur}
              value={formik.values.chatTitle}
              onChange={formik.handleChange}
              error={formik.touched.chatTitle && Boolean(formik.errors.chatTitle)}
              helperText={formik.touched.chatTitle && formik.errors.chatTitle}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
            <CommonButtonV1
              fullWidth
              variant="contained"
              backgroundColor={theme.palette.shades.primaryBrightDark}
              onClick={() => formik.handleSubmit()}
            >
              <Typography variant="textBase500" color="HighlightText">
                Start
              </Typography>
            </CommonButtonV1>
            <CommonButtonV1
              fullWidth
              variant="outlined"
              backgroundColor={theme.palette.white.main}
              onClick={() => {
                onClose();
                formik.resetForm();
              }}
              sx={{ border: `1px solid ${theme.palette.shades.primaryBrightDark}` }}
            >
              <Typography variant="textBase500" color={theme.palette.shades.primaryBrightDark}>
                {' '}
                Cancel
              </Typography>
            </CommonButtonV1>
          </Box>
        </Box>
      )}
    </CommonModalV1>
  );
}
